import { useState } from 'react'
import { ForumOutlined } from '@mui/icons-material'
import { Skeleton, Stack, Typography } from '@mui/material'
import { useStytchMember } from '@stytch/react/b2b'
import { Member } from '@stytch/vanilla-js'
import { QueryKey } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import { useGetAuthUsers } from '@/service-library/hooks/auth-user'
import { useDeleteDocumentComment } from '@/service-library/hooks/document-comments'
import { DocComment } from '@/types/comments'
import { showErrorSnackbar } from '@/utils/snackbars'
import CommentDeleteDialog from './CommentDeleteDialog'
import CommentListItem from './CommentListItem'

type CommentListProps = {
  comments: DocComment[]
  isLoading?: boolean
  /** The cached list query this panel reads from, so a delete can rewrite the matching item to a tombstone in place. */
  listQueryKey?: QueryKey
  /** The count badge's query key, invalidated on delete so the badge count stays
   * in sync with the list. */
  countQueryKey?: QueryKey
}

function LoadingSkeleton() {
  return (
    <Stack spacing={2.5} sx={{ p: 2 }}>
      {[0, 1, 2].map((key) => (
        <Stack key={key} direction="row" spacing={1.5}>
          <Skeleton variant="circular" width={32} height={32} />
          <Stack sx={{ flex: 1 }} spacing={0.75}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="90%" />
            <Skeleton variant="text" width="70%" />
          </Stack>
        </Stack>
      ))}
    </Stack>
  )
}

function EmptyState() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={1.5}
      sx={{ height: '100%', textAlign: 'center', px: 4, py: 6 }}
    >
      <ForumOutlined sx={{ fontSize: 48, color: 'text.disabled' }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        No comments yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Be the first to leave a comment on this document.
      </Typography>
    </Stack>
  )
}

export default function CommentList({
  comments,
  isLoading = false,
  listQueryKey,
  countQueryKey,
}: CommentListProps) {
  const { member } = useStytchMember()
  const stytchMember = member as Member
  const { usersById, isLoading: authorsLoading } = useGetAuthUsers(
    comments.map((comment) => comment.created_by_user_id),
  )
  const userId = stytchMember.external_id as string
  const usersWithCurrentUserId = {
    // Ensure the current user is always resolved, even if they haven't authored
    // any comments yet (e.g. when the list is empty).
    [userId]: { id: userId, name: stytchMember.name },
    ...usersById,
  }

  const deleteOverlay = useOverlay()
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const { deleteDocumentComment } = useDeleteDocumentComment({
    listQueryKey,
    sideEffectQueryKeys: [countQueryKey],
    onError: () => {
      showErrorSnackbar('Failed to delete comment. Please try again.')
    },
  })

  const requestDelete = (id: string) => {
    setPendingDeleteId(id)
    deleteOverlay.open()
  }

  const confirmDelete = () => {
    if (pendingDeleteId) deleteDocumentComment(pendingDeleteId)
    deleteOverlay.close()
  }

  // Keep the whole panel in the loading state until the comments and every
  // author have resolved, so names appear all at once rather than waterfalling.
  if (isLoading || authorsLoading) {
    return <LoadingSkeleton />
  }

  if (comments.length === 0) {
    return <EmptyState />
  }

  return (
    <>
      <Stack sx={{ py: 1 }}>
        {comments.map((comment) => (
          <CommentListItem
            key={comment.id}
            comment={comment}
            authorName={
              usersWithCurrentUserId[comment.created_by_user_id]?.name
            }
            isOwn={!!userId && comment.created_by_user_id === userId}
            onDeleteClick={() => requestDelete(comment.id)}
          />
        ))}
      </Stack>
      <CommentDeleteDialog
        overlay={deleteOverlay}
        onClose={deleteOverlay.close}
        onConfirm={confirmDelete}
      />
    </>
  )
}
